import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Loader2, AlertCircle, Link2 } from "lucide-react";
import { eventsApi } from "@/lib/api";
import { toSlug, copyText } from "@/lib/eventUrl";

export function SlugField({ value, onChange, title, eventId, savedSlug, inputCls }) {
  const [touched, setTouched] = useState(!!eventId);
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!touched) onChange(toSlug(title));
  }, [title, touched]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!value) { setCheck(null); return; }
    setChecking(true);
    const t = setTimeout(() => {
      eventsApi.checkSlug({ slug: value, title, exclude_id: eventId || "" })
        .then(setCheck).catch(() => setCheck(null)).finally(() => setChecking(false));
    }, 400);
    return () => clearTimeout(t);
  }, [value, eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!touched && check && !check.available && check.suggestion) onChange(check.suggestion);
  }, [check, touched]); // eslint-disable-line react-hooks/exhaustive-deps

  const url = `${window.location.origin}/rsvp/${value || "your-event"}`;
  const copy = async () => {
    await copyText(`${window.location.origin}/rsvp/${savedSlug || value}`);
    toast.success(savedSlug && savedSlug !== value ? "Copied the current live link (save to apply the new URL)" : "Event link copied");
  };

  return (
    <div data-testid="slug-field">
      <span className="mb-2 block text-sm font-medium">Custom Event URL (Slug)</span>
      <div className="flex flex-wrap gap-2">
        <div className="flex min-w-0 flex-1 items-center rounded-xl border border-input bg-card focus-within:border-gold focus-within:ring-2 focus-within:ring-gold/20">
          <span className="hidden whitespace-nowrap pl-4 text-sm text-muted-foreground sm:inline">/rsvp/</span>
          <input
            data-testid="event-slug-input"
            className={`${inputCls} border-0 bg-transparent focus:ring-0 sm:pl-1`}
            value={value}
            onChange={(e) => { setTouched(true); onChange(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-{2,}/g, "-")); }}
            onBlur={() => onChange(toSlug(value))}
            placeholder="pratik-weds-anushka"
          />
        </div>
        <button type="button" onClick={copy} disabled={!value} data-testid="copy-event-link-btn" className="inline-flex items-center gap-1.5 rounded-xl border border-border px-4 py-2.5 text-sm font-semibold transition-colors hover:bg-secondary disabled:opacity-50">
          <Copy className="h-4 w-4" /> Copy Event Link
        </button>
      </div>
      <p className="mt-2 flex items-center gap-1.5 break-all text-xs text-muted-foreground" data-testid="event-url-preview">
        <Link2 className="h-3.5 w-3.5 shrink-0 text-gold" /> {url}
      </p>
      <SlugStatus checking={checking} check={check} onUse={(s) => { setTouched(true); onChange(s); }} />
    </div>
  );
}

function SlugStatus({ checking, check, onUse }) {
  if (checking) return <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking availability…</p>;
  if (!check) return null;
  if (check.available) return <p className="mt-1 flex items-center gap-1.5 text-xs font-medium text-emerald-700" data-testid="slug-available"><Check className="h-3.5 w-3.5" /> This URL is available</p>;
  return (
    <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs font-medium text-destructive" data-testid="slug-error">
      <AlertCircle className="h-3.5 w-3.5" /> {check.error}
      {check.suggestion && (
        <button type="button" onClick={() => onUse(check.suggestion)} data-testid="slug-use-suggestion" className="ml-1 rounded-full bg-gold/15 px-2.5 py-0.5 font-semibold text-foreground hover:bg-gold/25">
          Use “{check.suggestion}”
        </button>
      )}
    </p>
  );
}
